import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Shield, MapPin, Users, Clock, FileText, Phone, Award, TrendingUp, DollarSign, Heart } from 'lucide-react';
import './TourOperatorChecklistPage.css';

export default function TourOperatorChecklistPage() {
  const { t } = useTranslation();

  const mustHaveCriteria = [
    {
      title: t('checklist.safety_quality'),
      icon: <Shield />,
      items: [
        t('checklist.safety_1'),
        t('checklist.safety_2'),
        t('checklist.safety_3'),
        t('checklist.safety_4')
      ]
    },
    {
      title: t('checklist.route_expertise'),
      icon: <MapPin />,
      items: [
        t('checklist.expertise_1'),
        t('checklist.expertise_2'),
        t('checklist.expertise_3')
      ]
    },
    {
      title: t('checklist.transparency'),
      icon: <FileText />,
      items: [
        t('checklist.transparency_1'),
        t('checklist.transparency_2'),
        t('checklist.transparency_3')
      ]
    },
    {
      title: t('checklist.group_tempo'),
      icon: <Users />,
      items: [
        t('checklist.group_1'),
        t('checklist.group_2'),
        t('checklist.group_3')
      ]
    },
    {
      title: t('checklist.logistics'),
      icon: <Clock />,
      items: [
        t('checklist.logistics_1'),
        t('checklist.logistics_2'),
        t('checklist.logistics_3')
      ]
    }
  ];

  const qualityFeatures = [
    t('checklist.quality_1'),
    t('checklist.quality_2'),
    t('checklist.quality_3'),
    t('checklist.quality_4'),
    t('checklist.quality_5'),
    t('checklist.quality_6'),
    t('checklist.quality_7')
  ];

  const redFlags = [
    t('checklist.redflag_1'),
    t('checklist.redflag_2'),
    t('checklist.redflag_3'),
    t('checklist.redflag_4'),
    t('checklist.redflag_5')
  ];

  const scottishSpecifics = [
    t('checklist.scotland_1'),
    t('checklist.scotland_2'),
    t('checklist.scotland_3'),
    t('checklist.scotland_4'),
    t('checklist.scotland_5')
  ];

  const questionsToAsk = [
    t('checklist.question_1'),
    t('checklist.question_2'),
    t('checklist.question_3'),
    t('checklist.question_4'),
    t('checklist.question_5'),
    t('checklist.question_6'),
    t('checklist.question_7')
  ];

  const scorecardCategories = [
    { name: t('checklist.score_safety'), points: 20, icon: <Shield /> },
    { name: t('checklist.score_expertise'), points: 20, icon: <MapPin /> },
    { name: t('checklist.score_logistics'), points: 15, icon: <Clock /> },
    { name: t('checklist.score_group'), points: 15, icon: <Users /> },
    { name: t('checklist.score_accommodation'), points: 10, icon: <Heart /> },
    { name: t('checklist.score_transparency'), points: 10, icon: <FileText /> },
    { name: t('checklist.score_price'), points: 10, icon: <DollarSign /> }
  ];

  return (
    <div className="checklist-page">
      <header className="checklist-header">
        <h1>{t('checklist.title')}</h1>
        <p className="checklist-subtitle">{t('checklist.subtitle')}</p>
      </header>

      {/* Introduction */}
      <section className="checklist-section intro-section">
        <p className="intro-text">{t('checklist.intro')}</p>
        <div className="rating-guideline">
          <Award size={32} />
          <div>
            <h3>{t('checklist.rating_guideline')}</h3>
            <p>{t('checklist.rating_text')}</p>
          </div>
        </div>
      </section>

      {/* Must-Have Criteria */}
      <section className="checklist-section">
        <h2 className="section-title must-have-title">
          <CheckCircle size={28} />
          {t('checklist.must_haves')}
        </h2>
        <p className="section-desc">{t('checklist.must_haves_desc')}</p>
        
        <div className="criteria-grid">
          {mustHaveCriteria.map((criteria, idx) => (
            <div key={idx} className="criteria-card">
              <div className="criteria-header">
                <div className="criteria-icon">{criteria.icon}</div>
                <h3>{criteria.title}</h3>
              </div>
              <ul className="criteria-list">
                {criteria.items.map((item, i) => (
                  <li key={i}>
                    <CheckCircle size={16} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Quality Features */}
      <section className="checklist-section">
        <h2 className="section-title quality-title">
          <TrendingUp size={28} />
          {t('checklist.quality_features')}
        </h2>
        <p className="section-desc">{t('checklist.quality_desc')}</p>
        
        <div className="features-list">
          {qualityFeatures.map((feature, idx) => (
            <div key={idx} className="feature-item">
              <CheckCircle size={18} />
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Scotland-Specific */}
      <section className="checklist-section scotland-section">
        <h2 className="section-title">
          <MapPin size={28} />
          {t('checklist.scotland_specifics')}
        </h2>
        <p className="section-desc">{t('checklist.scotland_desc')}</p>
        
        <div className="scotland-list">
          {scottishSpecifics.map((item, idx) => (
            <div key={idx} className="scotland-item">
              <MapPin size={18} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Red Flags */}
      <section className="checklist-section red-flags-section">
        <h2 className="section-title red-flag-title">
          <AlertTriangle size={28} />
          {t('checklist.red_flags')}
        </h2>
        <p className="section-desc">{t('checklist.red_flags_desc')}</p>
        
        <div className="red-flags-list">
          {redFlags.map((flag, idx) => (
            <div key={idx} className="red-flag-item">
              <AlertTriangle size={18} />
              <span>{flag}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Questions to Ask */}
      <section className="checklist-section">
        <h2 className="section-title">
          <Phone size={28} />
          {t('checklist.questions_title')}
        </h2>
        <p className="section-desc">{t('checklist.questions_desc')}</p>
        
        <div className="questions-list">
          {questionsToAsk.map((question, idx) => (
            <div key={idx} className="question-item">
              <span className="question-number">{idx + 1}</span>
              <span>{question}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Scorecard */}
      <section className="checklist-section scorecard-section">
        <h2 className="section-title">
          <Award size={28} />
          {t('checklist.scorecard_title')}
        </h2>
        <p className="section-desc">{t('checklist.scorecard_desc')}</p>
        
        <div className="scorecard">
          {scorecardCategories.map((category, idx) => (
            <div key={idx} className="scorecard-row">
              <div className="scorecard-category">
                {category.icon}
                <span>{category.name}</span>
              </div>
              <div className="scorecard-points">{category.points} {t('checklist.points')}</div>
            </div>
          ))}
          <div className="scorecard-total">
            <span>{t('checklist.total')}</span>
            <span className="total-points">100 {t('checklist.points')}</span>
          </div>
        </div>
        
        <div className="scorecard-note">
          <AlertTriangle size={20} />
          <p>{t('checklist.scorecard_note')}</p>
        </div>
      </section>

      {/* Decision Steps */}
      <section className="checklist-section steps-section">
        <h2 className="section-title">
          <TrendingUp size={28} />
          {t('checklist.steps_title')}
        </h2>
        
        <div className="steps-list">
          <div className="step-item">
            <span className="step-number">1</span>
            <div>
              <h4>{t('checklist.step1_title')}</h4>
              <p>{t('checklist.step1_text')}</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">2</span>
            <div>
              <h4>{t('checklist.step2_title')}</h4>
              <p>{t('checklist.step2_text')}</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">3</span>
            <div>
              <h4>{t('checklist.step3_title')}</h4>
              <p>{t('checklist.step3_text')}</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">4</span>
            <div>
              <h4>{t('checklist.step4_title')}</h4>
              <p>{t('checklist.step4_text')}</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">5</span>
            <div>
              <h4>{t('checklist.step5_title')}</h4>
              <p>{t('checklist.step5_text')}</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">6</span>
            <div>
              <h4>{t('checklist.step6_title')}</h4>
              <p>{t('checklist.step6_text')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="cta-box">
        <h3>{t('checklist.cta_title')}</h3>
        <p>{t('checklist.cta_text')}</p>
        <Link to="/guided-tours" className="btn btn-primary">
          {t('checklist.browse_operators')}
        </Link>
      </div>
    </div>
  );
}
